import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { DAYS_PT } from "@/lib/api";
import { TrendingUp, Camera } from "lucide-react";
import PhotoLightbox from "@/components/PhotoLightbox";

export default function PerformanceCharts() {
    const { children, activeChildId } = useApp();
    const { isChild } = useAuth();
    const activeChild = children.find((c) => c.id === activeChildId);
    const [stats, setStats] = useState({ days: [] });
    const [checkins, setCheckins] = useState([]);
    const [lightboxCheckin, setLightboxCheckin] = useState(null);

    const load = async () => {
        if (!activeChildId) return;
        const [s, c, t] = await Promise.all([
            api.get("/stats/weekly", { params: { child_id: activeChildId } }),
            api.get("/checkins", { params: { child_id: activeChildId } }),
            api.get("/tasks", { params: { child_id: activeChildId } }),
        ]);
        const taskTitleById = Object.fromEntries(t.data.map((x) => [x.id, x.title]));
        setStats(s.data);
        setCheckins(c.data.map((x) => ({ ...x, task_title: taskTitleById[x.task_id] })));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeChildId]);

    if (!activeChildId) {
        return <Card className="p-10 border-[#E2DCD5] text-center"><p className="text-slate-600">Adicione um filho primeiro.</p></Card>;
    }

    const chartData = stats.days.map((d) => ({
        name: DAYS_PT[d.dow],
        date: d.date,
        done: d.done,
        expected: d.expected,
        percent: d.percent,
    }));
    const avgPercent = chartData.length ? Math.round(chartData.reduce((a, d) => a + d.percent, 0) / chartData.length) : 0;
    const photoCheckins = checkins.filter((c) => c.photo_path).slice(0, 12);

    return (
        <div className="space-y-6">
            <div className="rise">
                <div className="text-xs uppercase tracking-widest text-[#8a7968]">Desempenho</div>
                <h1 className="text-3xl sm:text-4xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>
                    Semana de <span className="text-[#4A7C59]">{activeChild?.name}</span>
                </h1>
            </div>

            <div className="grid md:grid-cols-3 gap-4 rise rise-1">
                <Card className="p-5 border-[#E2DCD5]" data-testid="stat-avg-completion">
                    <div className="text-xs uppercase tracking-widest text-[#8a7968]">Média da semana</div>
                    <div className="text-3xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>{avgPercent}%</div>
                    <div className="text-xs text-slate-500 mt-1">Taxa de conclusão</div>
                </Card>
                <Card className="p-5 border-[#E2DCD5]">
                    <div className="text-xs uppercase tracking-widest text-[#8a7968]">Fotos registradas</div>
                    <div className="text-3xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>{checkins.filter((c) => c.photo_path).length}</div>
                    <div className="text-xs text-slate-500 mt-1">No total</div>
                </Card>
                <Card className="p-5 border-[#E2DCD5]">
                    <div className="text-xs uppercase tracking-widest text-[#8a7968]">Check-ins totais</div>
                    <div className="text-3xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>{checkins.length}</div>
                    <div className="text-xs text-slate-500 mt-1">Tarefas concluídas</div>
                </Card>
            </div>

            <Card className="p-6 border-[#E2DCD5] rise rise-2">
                <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="h-5 w-5 text-[#D96B43]" />
                    <h2 className="font-semibold" style={{ fontFamily: "Outfit" }}>Conclusão por dia (últimos 7 dias)</h2>
                </div>
                <div className="h-64" data-testid="weekly-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData}>
                            <CartesianGrid stroke="#E2DCD5" vertical={false} />
                            <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 12 }} axisLine={false} tickLine={false} />
                            <YAxis unit="%" tick={{ fill: "#64748B", fontSize: 12 }} axisLine={false} tickLine={false} domain={[0, 100]} />
                            <Tooltip
                                contentStyle={{ background: "#fff", border: "1px solid #E2DCD5", borderRadius: 12 }}
                                formatter={(v, _n, p) => [`${p.payload.done}/${p.payload.expected} (${v}%)`, "Concluído"]}
                            />
                            <Bar dataKey="percent" radius={[8, 8, 0, 0]}>
                                {chartData.map((d, i) => (
                                    <Cell key={i} fill={d.percent >= 70 ? "#4A7C59" : d.percent >= 40 ? "#E5A93C" : "#D96B43"} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </Card>

            <Card className="p-6 border-[#E2DCD5] rise rise-3">
                <div className="flex items-center gap-2 mb-4">
                    <Camera className="h-5 w-5 text-[#D96B43]" />
                    <h2 className="font-semibold" style={{ fontFamily: "Outfit" }}>Galeria de check-ins</h2>
                </div>
                {photoCheckins.length === 0 ? (
                    <p className="text-sm text-slate-500">Ainda sem fotos. Elas aparecem aqui depois dos check-ins com câmera.</p>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {photoCheckins.map((c) => (
                            <button
                                key={c.id}
                                onClick={() => setLightboxCheckin(c)}
                                className="relative rounded-xl overflow-hidden border border-[#E2DCD5] hover:ring-2 hover:ring-[#D96B43] transition"
                                data-testid={`gallery-${c.id}`}
                            >
                                <img
                                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${c.photo_path}`}
                                    alt=""
                                    className="w-full aspect-square object-cover"
                                    crossOrigin="use-credentials"
                                />
                                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-[11px] text-white">
                                    {new Date(c.date + "T00:00").toLocaleDateString("pt-BR")}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </Card>

            <PhotoLightbox
                open={!!lightboxCheckin}
                onOpenChange={(v) => !v && setLightboxCheckin(null)}
                checkin={lightboxCheckin}
                onInvalidated={load}
                canInvalidate={!isChild}
            />
        </div>
    );
}
